import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { getEnvironmentId } from '@utils/environmentId';
import { getCsrfToken, buildLoginUrl } from '../../contexts/AuthContext';
import { getIsOnline } from '../../services/connectivityService';

// Enterprise API calls are proxied through the VoltBot API BFF layer.
// The BFF adds the Bearer token server-side — no tokens in the browser.
const bffBaseUrl =
  (import.meta.env.VITE_VOLTBOT_API_BASE_URL ?? 'https://localhost:5002') +
  '/bff/enterprise';

export const enterpriseApiClient = axios.create({
  baseURL: bffBaseUrl,
  withCredentials: true, // Required for cookie auth
  headers: {
    'Content-Type': 'application/json',
    accept: 'text/plain'
  }
});

enterpriseApiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const environmentId = getEnvironmentId();
    if (environmentId) {
      config.headers['X-Environment-Id'] = environmentId;
    }

    // Add CSRF token for mutating requests
    if (['post', 'put', 'delete', 'patch'].includes(config.method ?? '')) {
      const token = getCsrfToken();
      if (token) {
        config.headers['X-CSRF-TOKEN'] = token;
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 401 handler with redirect debounce
let isRedirecting = false;

enterpriseApiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && !isRedirecting && getIsOnline()) {
      isRedirecting = true;
      setTimeout(() => {
        isRedirecting = false;
      }, 5000);
      window.location.href = buildLoginUrl(window.location.href);
    }
    return Promise.reject(error);
  }
);
